import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircleIcon, ChevronDownIcon, SearchIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
interface Country {
  code: string;
  flag: string;
  name: string;
  pattern: number[];
  maxDigits: number;
}
const COUNTRIES: Country[] = [{
  code: '+380',
  flag: '🇺🇦',
  name: 'Ukraine',
  pattern: [2, 3, 2, 2],
  maxDigits: 9
}, {
  code: '+48',
  flag: '🇵🇱',
  name: 'Poland',
  pattern: [3, 3, 3],
  maxDigits: 9
}, {
  code: '+49',
  flag: '🇩🇪',
  name: 'Germany',
  pattern: [3, 4, 4],
  maxDigits: 11
}, {
  code: '+44',
  flag: '🇬🇧',
  name: 'UK',
  pattern: [4, 3, 3],
  maxDigits: 10
}, {
  code: '+1',
  flag: '🇺🇸',
  name: 'USA / Canada',
  pattern: [3, 3, 4],
  maxDigits: 10
}, {
  code: '+420',
  flag: '🇨🇿',
  name: 'Czechia',
  pattern: [3, 3, 3],
  maxDigits: 9
}, {
  code: '+372',
  flag: '🇪🇪',
  name: 'Estonia',
  pattern: [4, 4],
  maxDigits: 8
}, {
  code: '+371',
  flag: '🇱🇻',
  name: 'Latvia',
  pattern: [4, 4],
  maxDigits: 8
}, {
  code: '+370',
  flag: '🇱🇹',
  name: 'Lithuania',
  pattern: [3, 3, 3],
  maxDigits: 8
}, {
  code: '+7',
  flag: '🇰🇿',
  name: 'Kazakhstan',
  pattern: [3, 3, 2, 2],
  maxDigits: 10
}, {
  code: '+373',
  flag: '🇲🇩',
  name: 'Moldova',
  pattern: [3, 3, 2],
  maxDigits: 8
}, {
  code: '+40',
  flag: '🇷🇴',
  name: 'Romania',
  pattern: [3, 3, 3],
  maxDigits: 9
}, {
  code: '+972',
  flag: '🇮🇱',
  name: 'Israel',
  pattern: [3, 3, 3],
  maxDigits: 9
}, {
  code: '+971',
  flag: '🇦🇪',
  name: 'UAE',
  pattern: [2, 3, 4],
  maxDigits: 9
}];
function formatPhone(digits: string, pattern: number[]): string {
  if (!digits) return '';
  const parts: string[] = [];
  let i = 0;
  for (const len of pattern) {
    if (i >= digits.length) break;
    parts.push(digits.slice(i, i + len));
    i += len;
  }
  if (i < digits.length) parts.push(digits.slice(i));
  return parts.join(' ');
}
interface UtmParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
}
function getUtmParams(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || sessionStorage.getItem('utm_source') || '',
    utm_medium: params.get('utm_medium') || sessionStorage.getItem('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
    utm_term: params.get('utm_term') || sessionStorage.getItem('utm_term') || '',
    utm_content: params.get('utm_content') || sessionStorage.getItem('utm_content') || ''
  };
}
export type HeroLeadFormVariant = 'primary' | 'violet' | 'amber';
interface VariantTokens {
  focusRing: string;
  focusBorder: string;
  buttonBg: string;
  buttonHoverBg: string;
  buttonShadow: string;
  buttonHoverShadow: string;
  spinnerBorder: string;
  successGradient: string;
  successShadow: string;
  selectedOptionBg: string;
  selectedOptionText: string;
}
const VARIANT_TOKENS: Record<HeroLeadFormVariant, VariantTokens> = {
  primary: {
    focusRing: 'focus:ring-primary-500/30 focus-within:ring-primary-500/30',
    focusBorder: 'focus:border-primary-500 focus-within:border-primary-500',
    buttonBg: 'from-primary-600 to-primary-500',
    buttonHoverBg: 'hover:from-primary-700 hover:to-primary-600',
    buttonShadow: 'shadow-primary-600/25',
    buttonHoverShadow: 'hover:shadow-primary-600/30',
    spinnerBorder: 'border-white/30 border-t-white',
    successGradient: 'from-emerald-400 to-emerald-600',
    successShadow: 'shadow-emerald-500/25',
    selectedOptionBg: 'bg-primary-50/60',
    selectedOptionText: 'text-primary-700'
  },
  violet: {
    focusRing: 'focus:ring-violet-500/30 focus-within:ring-violet-500/30',
    focusBorder: 'focus:border-violet-500 focus-within:border-violet-500',
    buttonBg: 'from-violet-600 to-violet-500',
    buttonHoverBg: 'hover:from-violet-700 hover:to-violet-600',
    buttonShadow: 'shadow-violet-600/30',
    buttonHoverShadow: 'hover:shadow-violet-600/40',
    spinnerBorder: 'border-white/30 border-t-white',
    successGradient: 'from-violet-400 to-violet-600',
    successShadow: 'shadow-violet-500/25',
    selectedOptionBg: 'bg-violet-50/60',
    selectedOptionText: 'text-violet-700'
  },
  amber: {
    focusRing: 'focus:ring-amber-500/30 focus-within:ring-amber-500/30',
    focusBorder: 'focus:border-amber-500 focus-within:border-amber-500',
    buttonBg: 'from-amber-600 to-amber-500',
    buttonHoverBg: 'hover:from-amber-700 hover:to-amber-600',
    buttonShadow: 'shadow-amber-600/30',
    buttonHoverShadow: 'hover:shadow-amber-600/40',
    spinnerBorder: 'border-white/30 border-t-white',
    successGradient: 'from-amber-400 to-amber-600',
    successShadow: 'shadow-amber-500/25',
    selectedOptionBg: 'bg-amber-50/60',
    selectedOptionText: 'text-amber-700'
  }
};
interface HeroLeadFormProps {
  positions?: string[];
  source?: string;
  variant?: HeroLeadFormVariant;
}
export function HeroLeadForm({
  positions,
  source = 'hero_form',
  variant = 'primary'
}: HeroLeadFormProps = {}) {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const positionOptions = positions ?? t.hero_form.positions;
  const tk = VARIANT_TOKENS[variant];
  const [name, setName] = useState('');
  const [country, setCountry] = useState<Country>(COUNTRIES[0]);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [position, setPosition] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    phone?: string;
    position?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [utmParams, setUtmParams] = useState<UtmParams>({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: ''
  });
  const countryRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const utms = getUtmParams();
    setUtmParams(utms);
    Object.entries(utms).forEach(([key, value]) => {
      if (value) sessionStorage.setItem(key, value);
    });
  }, []);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (countryRef.current && !countryRef.current.contains(e.target as Node)) {
        setCountryOpen(false);
      }
    }
    if (countryOpen) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [countryOpen]);
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, country.maxDigits);
    setPhoneDigits(digits);
    if (errors.phone) setErrors(p => ({
      ...p,
      phone: undefined
    }));
  };
  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!name.trim()) newErrors.name = t.hero_form.errors.name;
    if (!phoneDigits) {
      newErrors.phone = t.hero_form.errors.phone_required;
    } else if (phoneDigits.length < 7) {
      newErrors.phone = t.hero_form.errors.phone_invalid;
    }
    if (!position) newErrors.position = t.hero_form.errors.position;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const response = await fetch('https://formspree.io/f/mdalrzke', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name,
          phone: `${country.code} ${formatPhone(phoneDigits, country.pattern)}`,
          position,
          source,
          ...utmParams
        })
      });
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const data = await response.json();
        console.error('Formspree error:', data);
      }
    } catch (err) {
      console.error('Network error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted) {
    return <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="relative p-6 sm:p-7 rounded-2xl bg-white border border-emerald-100 shadow-2xl shadow-navy-900/10 flex items-center gap-4" data-id="element-972" data-mp="2ebbbnkh4fbpl">
        <div className={`flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br ${tk.successGradient} text-white flex items-center justify-center shadow-lg ${tk.successShadow}`} data-id="element-973" data-mp="1vlv8sqhyr760">
          <CheckCircleIcon className="w-6 h-6" data-id="element-974" data-mp="fw45y2mjiqct" />
        </div>
        <div data-id="element-975" data-mp="16pv0q3bg0vb">
          <h3 className="text-lg font-bold text-navy-900" data-id="element-976" data-mp="177nrcqrrwyh">
            {t.hero_form.success_title}
          </h3>
          <p className="text-sm text-navy-500" data-id="element-977" data-mp="66kjyo3j6gdq">
            {t.hero_form.success_subtitle}
          </p>
        </div>
      </motion.div>;
  }
  const inputCls = (hasError?: boolean) => `w-full px-3.5 py-3 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-all focus:outline-none focus:ring-2 ${tk.focusRing} ${tk.focusBorder} ${hasError ? 'border-red-400' : 'border-navy-200'}`;
  return <motion.form onSubmit={handleSubmit} initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6,
    delay: 0.35
  }} noValidate className="relative p-4 sm:p-5 rounded-2xl bg-white border border-navy-200/70 shadow-2xl shadow-navy-900/10" data-id="element-978" data-mp="ou7fe0k93wo9">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1.25fr_1.25fr_auto] gap-3 items-end" data-id="element-979" data-mp="3isx015ck7bbg">
        {/* Name */}
        <div data-id="element-980" data-mp="350qued718mq9">
          <label htmlFor="hero-name" className="block text-sm font-medium text-navy-700 mb-2" data-id="element-981" data-mp="394f3j0bbbqdm">
            {t.hero_form.name_label}
          </label>
          <input id="hero-name" type="text" value={name} onChange={e => {
          setName(e.target.value);
          if (errors.name) setErrors(p => ({
            ...p,
            name: undefined
          }));
        }} placeholder={t.hero_form.name_placeholder} className={inputCls(!!errors.name)} data-id="element-982" data-mp="2v6qf5ii1e13t" />
          {errors.name && <p className="mt-1 text-[11px] text-red-500 font-medium" data-id="element-983" data-mp="ocwdqvsyqy74">
              {errors.name}
            </p>}
        </div>

        {/* Phone with country code */}
        <div data-id="element-984" data-mp="24vg46f5dfy1n">
          <label htmlFor="hero-phone" className="block text-sm font-medium text-navy-700 mb-2" data-id="element-985" data-mp="24vy4pnz2gyfd">
            {t.hero_form.phone_label}
          </label>
          <div className={`flex items-center rounded-xl border bg-white transition-all focus-within:ring-2 ${tk.focusRing} ${tk.focusBorder} ${errors.phone ? 'border-red-400' : 'border-navy-200'}`} data-id="element-986" data-mp="2dua52hpqjal0">
            <div ref={countryRef} className="relative flex-shrink-0" data-id="element-987" data-mp="2nkmlbxqiohlc">
              <button type="button" onClick={() => setCountryOpen(o => !o)} className="h-full flex items-center gap-1 pl-3 pr-2 py-3 hover:bg-navy-50/60 rounded-l-xl transition-colors" aria-haspopup="listbox" aria-expanded={countryOpen} aria-label="Country code" data-id="element-988" data-mp="31l5q8vzor7ja">
                <span className="text-lg leading-none" data-id="element-989" data-mp="32ab08pe7p0zg">{country.flag}</span>
                <ChevronDownIcon className={`w-3.5 h-3.5 text-navy-400 transition-transform ${countryOpen ? 'rotate-180' : ''}`} data-id="element-990" data-mp="32ce1k5ktegjh" />
              </button>
              {countryOpen && <motion.ul initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.15
            }} role="listbox" className="absolute z-30 mt-1.5 left-0 w-64 max-h-64 overflow-y-auto rounded-xl bg-white border border-navy-200 shadow-xl shadow-navy-900/15 py-1" data-id="element-991" data-mp="3g9kidulza562">
                  {COUNTRIES.map(c => <li key={`${c.code}-${c.name}`} role="option" aria-selected={country.code === c.code} data-id="element-992" data-mp="csu64xzsyvvx">
                      <button type="button" onClick={() => {
                  setCountry(c);
                  setPhoneDigits('');
                  setCountryOpen(false);
                }} className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-navy-50 transition-colors ${country.code === c.code ? `${tk.selectedOptionBg} ${tk.selectedOptionText}` : 'text-navy-700'}`} data-id="element-993" data-mp="1sc1ip1po67nj">
                        <span className="text-base" data-id="element-994" data-mp="278ishoi7ne7z">{c.flag}</span>
                        <span className="flex-1" data-id="element-995" data-mp="276zek4k11f9d">{c.name}</span>
                        <span className="text-navy-400 tabular-nums" data-id="element-996" data-mp="229pnylkwixjy">
                          {c.code}
                        </span>
                      </button>
                    </li>)}
                </motion.ul>}
            </div>
            <span className="pl-1 pr-1.5 text-sm font-medium text-navy-900 tabular-nums select-none" data-id="element-997" data-mp="2ej4858uglupd">
              {country.code}
            </span>
            <input id="hero-phone" type="tel" inputMode="numeric" value={formatPhone(phoneDigits, country.pattern)} onChange={handlePhoneChange} placeholder={country.code === '+380' ? '(00) 000-00-00' : 'XXX XXX XXX'} className="flex-1 min-w-0 pr-3 py-3 bg-transparent text-navy-900 placeholder-navy-400 text-sm focus:outline-none tabular-nums" data-id="element-998" data-mp="2jgdwel6440bm" />
          </div>
          {errors.phone && <p className="mt-1 text-[11px] text-red-500 font-medium" data-id="element-999" data-mp="2tg4qilqrgdb6">
              {errors.phone}
            </p>}
        </div>

        {/* Position dropdown */}
        <div data-id="element-1000" data-mp="3h4e6v857g8ux">
          <label htmlFor="hero-position" className="block text-sm font-medium text-navy-700 mb-2" data-id="element-1001" data-mp="3lwp7yebom0p2">
            {t.hero_form.position_label}
          </label>
          <div className="relative" data-id="element-1002" data-mp="3ra5oxwwhepnq">
            <select id="hero-position" value={position} onChange={e => {
            setPosition(e.target.value);
            if (errors.position) setErrors(p => ({
              ...p,
              position: undefined
            }));
          }} className={`w-full appearance-none pl-3.5 pr-10 py-3 rounded-xl border bg-white text-sm transition-all focus:outline-none focus:ring-2 ${tk.focusRing} ${tk.focusBorder} ${errors.position ? 'border-red-400' : 'border-navy-200'} ${position ? 'text-navy-900' : 'text-navy-400'}`} data-id="element-1003" data-mp="3r94ftxz10d8n">
              <option value="" disabled data-id="element-1004" data-mp="3hecf4la5da9n">
                {t.hero_form.position_placeholder}
              </option>
              {positionOptions.map(p => <option key={p} value={p} className="text-navy-900" data-id="element-1005" data-mp="wkz8penk7c4v">
                  {p}
                </option>)}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" data-id="element-1006" data-mp="18o1y8bippzx0" />
          </div>
          {errors.position && <p className="mt-1 text-[11px] text-red-500 font-medium" data-id="element-1007" data-mp="1dl1zm94maehs">
              {errors.position}
            </p>}
        </div>

        {/* Submit button */}
        <div className="sm:col-span-2 lg:col-span-1" data-id="element-1008" data-mp="l026b89o03gu">
          <button type="submit" disabled={isSubmitting} className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r ${tk.buttonBg} rounded-xl ${tk.buttonHoverBg} transition-all shadow-lg ${tk.buttonShadow} hover:shadow-xl ${tk.buttonHoverShadow} disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap min-h-[46px]`} data-id="element-1009" data-mp="pxao37qwi5hk">
            {isSubmitting ? <>
                <div className={`w-4 h-4 border-2 ${tk.spinnerBorder} rounded-full animate-spin`} data-id="element-1010" data-mp="3vsxuex60yu9m" />
                {t.hero_form.submitting}
              </> : <>
                <SearchIcon className="w-4 h-4" data-id="element-1011" data-mp="3ltypnpodrirw" />
                {t.hero_form.submit}
                <ArrowRightIcon className="w-4 h-4" data-id="element-1012" data-mp="3gw6qcaleoudo" />
              </>}
          </button>
        </div>
      </div>

      <p className="mt-3 text-[11px] text-navy-400 leading-relaxed" data-id="element-1013" data-mp="3cuva2vof9li5">
        {t.hero_form.privacy}
      </p>
    </motion.form>;
}